import React, { useState } from 'react'
import API from '../../../services/API'
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  TextField,
  Typography,
} from '@mui/material'
import SmallModal from '../../../components/Modals/SmallModal'
import AnDatePicker from '../../../components/DateTime/DatePicker'

// Suggests the next version number: whole versions step by 1, decimal
// versions by 0.1 (rounded to avoid float noise).
const suggestNextVersion = (currentVersion) => {
  if (!Number.isFinite(currentVersion)) return ''
  if (Number.isInteger(currentVersion)) return String(currentVersion + 1)
  return String(Math.round((currentVersion + 0.1) * 100) / 100)
}

const PDF_ONLY_MESSAGE =
  'Policies must be uploaded as PDF. In Word use File → Save As → PDF.'

// The picker's accept filter is only a hint ("All files" or drag-and-drop
// skips it), and staff read policies in a PDF viewer, so check it ourselves.
const isPdfFile = (file) =>
  file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '')

const AddNewPolicy = ({ policyDescription, currentVersion }) => {
  const hasCurrentVersion = Number.isFinite(currentVersion)
  const [version, setVersion] = useState(suggestNextVersion(currentVersion))
  const [versionChanges, setVersionChanges] = useState('')
  const [date, setDate] = useState(null)
  const [reviewDate, setReviewDate] = useState(null)
  const [selectedFile, setSelectedFile] = useState()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [confirmHistorical, setConfirmHistorical] = useState(false)

  const numericVersion = Number(version)
  const isHistorical = hasCurrentVersion && numericVersion <= currentVersion

  const submitPolicy = async (allowHistorical) => {
    if (!isPdfFile(selectedFile)) {
      setError(PDF_ONLY_MESSAGE)
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const urlRes = await API.post('gets3uploadurl', {
        folder: 'governance/policies',
        extension: 'pdf',
      })
      if (urlRes.status !== 200 || !urlRes.data?.url) {
        throw new Error('Could not prepare the document upload. Please try again.')
      }
      const putRes = await API.put(urlRes.data.url, selectedFile, {
        headers: {
          'Content-Type': 'application/pdf',
        },
      })
      if (putRes.status !== 200) {
        throw new Error('The document failed to upload. Please try again.')
      }
      const res = await API.post('/policy', {
        policyDescription: policyDescription._id,
        version,
        versionChanges,
        date,
        reviewDate,
        docLink: urlRes.data.key,
        allowHistorical,
      })
      if (res.status !== 200) {
        throw new Error(
          res.data?.error ||
            'The policy version could not be saved. Please try again.',
        )
      }
      window.location.reload()
    } catch (e) {
      console.error(e)
      setError(e.message || 'Something went wrong. Please try again.')
      setSubmitting(false)
    }
  }

  const handleAddNewSubmit = (e) => {
    e.preventDefault()
    if (!selectedFile) return
    if (isHistorical) {
      setConfirmHistorical(true)
      return
    }
    submitPolicy(false)
  }

  return (
    <Box
      component="form"
      onSubmit={handleAddNewSubmit}
      display="flex"
      flexDirection="column"
      gap={1}
      width="100%"
    >
      <Typography variant="title3">Add New Policy</Typography>
      <TextField
        value={policyDescription.policyTitle}
        disabled
        label="Policy Title"
        size="small"
      />
      <TextField
        value={version}
        onChange={(e) => setVersion(e.target.value)}
        size="small"
        label="Version"
        autoComplete="off"
        type="number"
        inputProps={{ step: 'any', min: 0 }}
        required
        helperText={
          hasCurrentVersion
            ? `Current version is ${currentVersion} — the highest version number is always shown to staff, so enter a higher number (note 2.10 counts as 2.1, use 2.2 instead).`
            : 'This is the first version of this policy.'
        }
      />
      {isHistorical && (
        <Alert severity="warning">
          Version {currentVersion} is already the current version. This upload
          will be stored as a historical version and will NOT become the policy
          staff see.
        </Alert>
      )}
      <TextField
        value={versionChanges}
        onChange={(e) => setVersionChanges(e.target.value)}
        multiline
        minRows={2}
        label="Summary of Changes"
        autoComplete="off"
        required
      />
      <AnDatePicker
        label="Date"
        value={date}
        required
        onChange={(newDate) => setDate(newDate)}
      />
      <AnDatePicker
        label="Review Date"
        value={reviewDate}
        onChange={(newDate) => setReviewDate(newDate)}
      />
      <TextField
        type="file"
        size="small"
        defaultValue={selectedFile}
        inputProps={{ accept: 'application/pdf,.pdf' }}
        onChange={(e) => {
          const file = e.target.files[0]
          if (file && !isPdfFile(file)) {
            e.target.value = ''
            setSelectedFile(undefined)
            setError(PDF_ONLY_MESSAGE)
            return
          }
          setError('')
          setSelectedFile(file)
        }}
        helperText="PDF only"
        required
      />
      {error && <Alert severity="error">{error}</Alert>}
      <Button
        variant="contained"
        color="primary"
        type="submit"
        disabled={submitting}
      >
        {submitting ? 'Uploading…' : 'Submit'}
      </Button>
      <SmallModal
        open={confirmHistorical}
        onClose={() => setConfirmHistorical(false)}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography fontWeight={700}>
            Store as a historical version?
          </Typography>
          <Typography variant="body2">
            Version {version} is not higher than the current version (
            {currentVersion}), so it will not become the policy staff see or
            acknowledge. Only continue if you are deliberately back-filling an
            older version. If this is meant to be the new current policy,
            cancel and enter a version higher than {currentVersion}.
          </Typography>
          <ButtonGroup fullWidth>
            <Button
              color="inherit"
              onClick={() => setConfirmHistorical(false)}
            >
              Cancel
            </Button>
            <Button
              color="warning"
              variant="contained"
              disabled={submitting}
              onClick={() => {
                setConfirmHistorical(false)
                submitPolicy(true)
              }}
            >
              Store as Historical
            </Button>
          </ButtonGroup>
        </Box>
      </SmallModal>
    </Box>
  )
}

export default AddNewPolicy
